import { css } from "@emotion/react";
import { useState } from "react";

import { SubmitAction } from "../../action/Action";
import { PromiseToast } from "../../components/async/PromiseToast";
import Button from "../../components/Button/Button";
import Centered from "../../components/Centered";
import Form from "../../components/form/Form";
import type { TextFieldProps } from "../../components/form/TextField";
import TextField from "../../components/form/TextField";
import useField from "../../components/form/useField";
import { getErrorMessage, validationFn } from "../../components/form/utilities";
import Grid from "../../components/Grid";
import Spacer from "../../components/space/Spacer";
import Text from "../../components/Text";
import { callOutgoingRequest } from "../../data/tanstack-query";
import { createAnonymousId, signupAnonymous } from "../../endpoints/outgoing-requests";
import useSubmit from "../../hooks/useSubmit";
import {
    type AnonymousSignupStep,
    getIndexLocation,
    goToIndex,
    useIndexLocationState,
} from "./navigation";

const tokenCss = css({
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
    fontSize: "1.15rem",
    letterSpacing: "0.04em",
    wordBreak: "break-word",
    textAlign: "center",
});

function normalizeAnonymousId(value: string) {
    return value.toLowerCase().replace(/[-\s]/g, "");
}

function anonymousLocation(step: AnonymousSignupStep, code: string | null) {
    return getIndexLocation({
        action: "signup",
        method: "anonymous",
        signup_anonymous: { step, code },
    });
}

export default function AnonymousSignupScreen() {
    const { signup_anonymous } = useIndexLocationState();
    const code = signup_anonymous?.code ?? null;
    const step = code === null ? "explain" : (signup_anonymous?.step ?? "explain");
    const [loadError, setLoadError] = useState<unknown>(null);
    const [loading, setLoading] = useState(false);

    const [revealId] = useSubmit(async (): Promise<void> => {
        setLoadError(null);
        setLoading(true);
        try {
            const result = await callOutgoingRequest(createAnonymousId());
            goToIndex({
                action: "signup",
                method: "anonymous",
                signup_anonymous: { step: "reveal", code: result.id },
            });
        } catch (error) {
            setLoadError(error);
        } finally {
            setLoading(false);
        }
    }, []);

    return (
        <>
            {step === "explain" || code === null ? (
                <ExplainAnonymousIdStep error={loadError} loading={loading} onContinue={revealId} />
            ) : step === "reveal" ? (
                <RevealAnonymousIdStep anonymousId={code} />
            ) : step === "confirm" ? (
                <ConfirmAnonymousIdStep anonymousId={code} />
            ) : (
                <ChoosePasswordStep anonymousId={code} />
            )}
        </>
    );
}

function ExplainAnonymousIdStep(props: {
    error: unknown;
    loading: boolean;
    onContinue: () => void;
}) {
    const { error, loading, onContinue } = props;
    return (
        <Grid columns={1} gap={4}>
            <Text variant="body">
                Un identifiant unique vous sera donné une seule fois. Notez-le dans un endroit sûr :
                il vous servira à vous reconnecter, et ne pourra plus être affiché ensuite.
            </Text>
            <Text variant="body">
                Vous devrez le ressaisir pour confirmer que vous l'avez bien conservé, puis choisir
                un mot de passe.
            </Text>
            {error ? <Text variant="body">{getErrorMessage(error)}</Text> : null}
            {loading ? (
                <Text variant="body">Génération de votre identifiant...</Text>
            ) : (
                <Centered>
                    <Button label="Afficher mon identifiant" action={onContinue} />
                </Centered>
            )}
        </Grid>
    );
}

function RevealAnonymousIdStep(props: { anonymousId: string }) {
    const { anonymousId } = props;
    return (
        <Grid columns={1} gap={4}>
            <Text variant="body">
                Voici votre identifiant anonyme. Notez-le dans un endroit sûr. Il ne vous sera plus
                jamais montré.
            </Text>
            <div css={tokenCss}>{anonymousId}</div>
            <Centered>
                <Button
                    label="Copier"
                    role="side"
                    action={() => navigator.clipboard.writeText(anonymousId)}
                />
            </Centered>
            <Centered>
                <Button
                    label="J'ai noté cet identifiant"
                    action={anonymousLocation("confirm", anonymousId)}
                />
            </Centered>
        </Grid>
    );
}

function ConfirmAnonymousIdStep(props: { anonymousId: string }) {
    const { anonymousId } = props;
    const confirmField = useField<TextFieldProps>({
        value: undefined,
        label: "Identifiant anonyme",
        required: true,
        validator: validationFn("anonymous-id-match", (value) => {
            if (normalizeAnonymousId(value) !== normalizeAnonymousId(anonymousId)) {
                throw new Error("Cet identifiant ne correspond pas");
            }
        }),
    });
    const onSubmit = () => {
        try {
            confirmField.validate();
        } catch {
            return;
        }
        goToIndex({
            action: "signup",
            method: "anonymous",
            signup_anonymous: { step: "password", code: anonymousId },
        });
    };
    return (
        <>
            <Text variant="body">
                Saisissez à nouveau votre identifiant pour vérifier que vous l'avez bien conservé.
            </Text>
            <Spacer scale={4} />
            <Form onSubmit={onSubmit}>
                <Grid columns={1} gap={4}>
                    <TextField {...confirmField.props} />
                    <Centered>
                        <Button label="Continuer" action={SubmitAction} />
                    </Centered>
                </Grid>
            </Form>
        </>
    );
}

function ChoosePasswordStep(props: { anonymousId: string }) {
    const { anonymousId } = props;
    const passwordField = useField<TextFieldProps>({
        value: undefined,
        label: "Mot de passe",
        type: "password",
        required: true,
    });
    const confirmationField = useField<TextFieldProps>({
        value: undefined,
        label: "Confirmer le mot de passe",
        type: "password",
        required: true,
        validator: validationFn(`match-${passwordField.props.value ?? ""}`, (value) => {
            if (value !== passwordField.props.value) {
                throw new Error("Les mots de passe ne correspondent pas");
            }
        }),
    });
    const [onSubmit, submission] = useSubmit(async (): Promise<void> => {
        const password = passwordField.validate();
        confirmationField.validate();
        await callOutgoingRequest(signupAnonymous({ id: anonymousId, password }));
    }, [anonymousId]);
    return (
        <>
            <PromiseToast promise={submission} message="Inscription enregistrée" />
            <Text variant="body">Choisissez un mot de passe.</Text>
            <Spacer scale={4} />
            <Form onSubmit={onSubmit}>
                <Grid columns={1} gap={4}>
                    <TextField {...passwordField.props} />
                    <TextField {...confirmationField.props} />
                    <Centered>
                        <Button label="Créer mon compte" action={SubmitAction} />
                    </Centered>
                </Grid>
            </Form>
        </>
    );
}
